/**
 * <plus-footer-two>
 * Small enhancements for the "Footer V2" section:
 * - Link-list columns are <details> elements. On phones (when the section allows it) they start
 *   collapsed and toggle normally; on wider screens they are forced open and cannot be collapsed.
 *   Without JavaScript every list simply stays open.
 * - The country/language form submits as soon as a select changes, so its fallback
 *   "Update" button can be hidden.
 */
const DESKTOP_QUERY = window.matchMedia('(min-width: 750px)');

class PlusFooterTwo extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.handleClick);
    this.addEventListener('change', this.handleChange);
    this.addEventListener('shopify:block:select', this.handleBlockSelect);
    DESKTOP_QUERY.addEventListener('change', this.applyLayout);

    this.querySelectorAll('[data-autosubmit]').forEach((select) => {
      select.closest('form')?.classList.add('plus-footer2__localization--js');
    });

    this.applyLayout();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('change', this.handleChange);
    this.removeEventListener('shopify:block:select', this.handleBlockSelect);
    DESKTOP_QUERY.removeEventListener('change', this.applyLayout);
  }

  /** Theme editor: open a collapsed link list when its block is selected. @param {Event} event */
  handleBlockSelect = (event) => {
    const block = event.target instanceof Element ? event.target : null;
    const menu = block?.querySelector('details[data-footer-menu]');
    if (menu instanceof HTMLDetailsElement) menu.open = true;
  };

  get collapsible() {
    return Boolean(this.closest('.plus-footer2--collapsible'));
  }

  get menus() {
    return /** @type {HTMLDetailsElement[]} */ (Array.from(this.querySelectorAll('details[data-footer-menu]')));
  }

  /** Open every menu on wider screens; collapse them on phones when the section allows it. */
  applyLayout = () => {
    const collapse = this.collapsible && !DESKTOP_QUERY.matches;
    this.menus.forEach((menu) => {
      menu.open = !collapse;
      // Only a real toggle should be a tab stop.
      const summary = menu.querySelector('summary');
      if (summary) summary.tabIndex = collapse ? 0 : -1;
    });
  };

  /** @param {MouseEvent} event */
  handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const summary = target?.closest('summary');
    if (!summary || !this.contains(summary)) return;
    const menu = summary.parentElement;
    if (!(menu instanceof HTMLDetailsElement) || !menu.hasAttribute('data-footer-menu')) return;

    // Menus only toggle on phones, and only when the section is set to collapse them.
    if (DESKTOP_QUERY.matches || !this.collapsible) event.preventDefault();
  };

  /** @param {Event} event */
  handleChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.hasAttribute('data-autosubmit')) return;
    const form = select.form;
    if (!form) return;
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.submit();
  };
}

if (!customElements.get('plus-footer-two')) {
  customElements.define('plus-footer-two', PlusFooterTwo);
}
